/**
 *
 * This is based on `CRM_INTEGRATION_FIELDS` in the monorepo's
 * `code/packages/shared/src/types/crmIntegrations.ts`, which the apiserver validates writes
 * against.
 */
import { EstimateType } from "../../../shared/common/EstimateEngine/EstimateEngineTypes";

export enum CRMIntegrationType {
	JOB_NIMBUS = "JOB_NIMBUS",
	XCELERATE = "XCELERATE",
	FIRST_ATLANTIC = "FIRST_ATLANTIC",
	ALBI = "ALBI",
	PSA = "PSA",
}

type CRMIntegrationBase = {
	type: CRMIntegrationType;
	enabled: boolean;
};

export type JobNimbusIntegration = CRMIntegrationBase & {
	type: CRMIntegrationType.JOB_NIMBUS;
	apiKey: string;
	estimateTypesToSync: EstimateType[];
};

export type XcelerateIntegration = CRMIntegrationBase & {
	type: CRMIntegrationType.XCELERATE;
	apiKey: string;
	webhookUrl?: string;
	autoRegisterWebhook: boolean;
	enableAssignee: boolean;
};

export type FirstAtlanticIntegration = CRMIntegrationBase & {
	type: CRMIntegrationType.FIRST_ATLANTIC;
	apiKey: string;
	outboundApiKey?: string;
};

export type AlbiIntegration = CRMIntegrationBase & {
	type: CRMIntegrationType.ALBI;
	id: string;
	apiKey: string;
	webhookUrl?: string;
	autoRegisterWebhook: boolean;
};

export type PSAIntegration = CRMIntegrationBase & {
	type: CRMIntegrationType.PSA;
	baseUrl: string;
	schemaId: string;
	apiKey: string;
	syncType: number;
	uploadFolderPath?: string;
};

export type CRMIntegration =
	| JobNimbusIntegration
	| XcelerateIntegration
	| FirstAtlanticIntegration
	| AlbiIntegration
	| PSAIntegration;

export const CRM_INTEGRATION_LABELS: Record<CRMIntegrationType, string> = {
	[CRMIntegrationType.JOB_NIMBUS]: "JobNimbus",
	[CRMIntegrationType.XCELERATE]: "Xcelerate",
	[CRMIntegrationType.FIRST_ATLANTIC]: "First Atlantic",
	[CRMIntegrationType.ALBI]: "Albi",
	[CRMIntegrationType.PSA]: "PSA",
};

export type CRMIntegrationFieldKind = "string" | "boolean" | "number" | "url" | "slug" | "estimateTypes";

/** A `slug` field must match this: lowercase letters, digits and hyphens. */
export const CRM_INTEGRATION_SLUG_PATTERN = /^[a-z0-9-]+$/;

export type CRMIntegrationFieldOption = { value: string; label: string };

export type CRMIntegrationField = {
	key: string;
	kind: CRMIntegrationFieldKind;
	required: boolean;
	/** Must not repeat across entries of the same integration type. */
	unique?: boolean;
	lockedReason?: string;
	secret: boolean;
	label: string;
	helpText?: string;
	options?: CRMIntegrationFieldOption[];
};

const ESTIMATE_TYPE_OPTIONS: CRMIntegrationFieldOption[] = [
	{ value: "MITIGATION", label: "Mitigation" },
	{ value: "RECONSTRUCTION", label: "Reconstruction" },
	{ value: "CONTENTS", label: "Contents" },
	{ value: "MOLD", label: "Mold" },
	{ value: "FIRE_MITIGATION", label: "Fire Mitigation" },
	{ value: "BIOHAZARD", label: "Biohazard" },
	{ value: "TIME_AND_MATERIAL", label: "Time and Material" },
];

export const CRM_INTEGRATION_FIELDS: Record<CRMIntegrationType, CRMIntegrationField[]> = {
	[CRMIntegrationType.JOB_NIMBUS]: [
		{ key: "apiKey", kind: "string", required: true, secret: true, label: "API Key" },
		{
			key: "estimateTypesToSync",
			kind: "estimateTypes",
			required: true,
			secret: false,
			label: "Estimate Types to Sync",
			helpText: "Which estimate types to create from incoming JobNimbus jobs. Jobs whose type isn't listed are skipped.",
			options: ESTIMATE_TYPE_OPTIONS,
		},
	],
	[CRMIntegrationType.XCELERATE]: [
		{
			key: "apiKey",
			kind: "string",
			required: true,
			secret: true,
			label: "API Key",
			helpText: "Bearer token for authenticating to the Xcelerate REST API.",
		},
		{
			key: "autoRegisterWebhook",
			kind: "boolean",
			required: true,
			secret: false,
			label: "Auto-register Webhook",
			helpText: "Whether to auto-register the webhook with Xcelerate on server startup.",
		},
		{
			key: "enableAssignee",
			kind: "boolean",
			required: true,
			secret: false,
			label: "Enable Assignee",
			helpText: "Whether to map the Xcelerate estimator to a Rebuild user and auto-assign them.",
		},
		{
			key: "webhookUrl",
			kind: "string",
			required: false,
			secret: false,
			label: "Webhook URL Override",
			helpText: "Optional webhook delivery URL override (e.g. ngrok for local dev).",
		},
	],
	[CRMIntegrationType.FIRST_ATLANTIC]: [
		{
			key: "apiKey",
			kind: "string",
			required: true,
			secret: true,
			label: "Inbound API Key",
			helpText: 'API key First Atlantic sends in the "Authorization" header on incoming webhook requests.',
		},
		{
			key: "outboundApiKey",
			kind: "string",
			required: false,
			secret: true,
			label: "Outbound API Key",
			helpText: "API key used when calling the First Atlantic API (e.g. add-job-log).",
		},
	],
	[CRMIntegrationType.ALBI]: [
		{
			key: "id",
			kind: "slug",
			required: true,
			unique: true,
			secret: false,
			label: "Instance ID",
			lockedReason:
				"Albi delivers webhooks to a URL containing this id. Changing it requires re-registering the webhook.",
			helpText:
				'Unique identifier for this Albi instance',
		},
		{
			key: "apiKey",
			kind: "string",
			required: true,
			secret: true,
			label: "API Key",
			helpText: 'API key sent as the "ApiKey" header for all Albi API calls.',
		},
		{
			key: "autoRegisterWebhook",
			kind: "boolean",
			required: true,
			secret: false,
			label: "Auto-register Webhook",
			helpText: "Whether to auto-register the webhook with Albi on server startup.",
		},
		{
			key: "webhookUrl",
			kind: "string",
			required: false,
			secret: false,
			label: "Webhook URL Override",
			helpText: "Webhook delivery URL override (e.g. ngrok for local dev).",
		},
	],
	[CRMIntegrationType.PSA]: [
		{
			key: "baseUrl",
			kind: "url",
			required: true,
			secret: false,
			label: "Base URL",
			helpText: "PSA deployment root, e.g. https://beta.psarcweb.com. Beta and production are separate deployments.",
		},
		{ key: "schemaId", kind: "string", required: true, secret: false, label: "Schema ID" },
		{ key: "apiKey", kind: "string", required: true, secret: true, label: "API Key" },
		{ key: "syncType", kind: "number", required: true, secret: false, label: "Sync Type" },
		{
			key: "uploadFolderPath",
			kind: "string",
			required: false,
			secret: false,
			label: "Upload Folder Path",
			helpText:
				'Optional root folder to nest Rebuild\'s uploads under, e.g. "Rebuild". Default is for folders to sit at the top of the job\'s attachments.',
		},
	],
};

/** Fields present on every integration regardless of type. */
export const CRM_INTEGRATION_BASE_KEYS = ["type", "enabled"];

/** Fresh empty value per call — a shared [] would alias across integrations. */
const emptyValueForKind = (kind: CRMIntegrationFieldKind): unknown => {
	switch (kind) {
		case "string":
		case "url":
		case "slug":
			return "";
		case "boolean":
			return false;
		case "number":
			return 0;
		case "estimateTypes":
			return [];
	}
};

export function defaultsForType(type: CRMIntegrationType): CRMIntegration {
	const result: Record<string, unknown> = { type, enabled: false };
	CRM_INTEGRATION_FIELDS[type].forEach((field) => {
		if (field.required) {
			result[field.key] = emptyValueForKind(field.kind);
		}
	});
	return result as CRMIntegration;
}

const ESTIMATE_TYPE_VALUES = new Set<string>(ESTIMATE_TYPE_OPTIONS.map((option) => option.value));

function isHttpUrl(value: string): boolean {
	try {
		const parsed = new URL(value);
		return parsed.protocol === "http:" || parsed.protocol === "https:";
	} catch {
		return false;
	}
}

export function serializeIntegration(integration: CRMIntegration): CRMIntegration {
	const source: Record<string, unknown> = integration;
	const result: Record<string, unknown> = {
		type: integration.type,
		enabled: integration.enabled === true,
	};

	CRM_INTEGRATION_FIELDS[integration.type].forEach((field) => {
		const value = source[field.key];
		switch (field.kind) {
			case "string":
			case "url":
			case "slug": {
				const trimmed = typeof value === "string" ? value.trim() : "";
				if (trimmed !== "") {
					result[field.key] = trimmed;
				}
				break;
			}
			case "number":
				if (typeof value === "number" && Number.isFinite(value)) {
					result[field.key] = value;
				}
				break;
			case "boolean":
				result[field.key] = value === true;
				break;
			case "estimateTypes":
				result[field.key] = Array.isArray(value) ? value : [];
				break;
		}
	});

	return result as CRMIntegration;
}

/**
 * Coerces a stored value to the kind its field field declares.
 */
function coerceToKind(kind: CRMIntegrationFieldKind, value: unknown): unknown {
	switch (kind) {
		case "string":
		case "url":
		case "slug":
			return typeof value === "string" ? value : String(value);
		case "number": {
			if (typeof value === "number") {
				return value;
			}
			const parsed = Number(String(value).trim());
			return Number.isFinite(parsed) ? parsed : NaN;
		}
		case "boolean":
			return value === true || value === "true";
		case "estimateTypes":
			return Array.isArray(value) ? value : [];
	}
}

/**
 * Normalizes one entry read from the server onto the shape this UI edits: known fields are
 * coerced to their declared kind and copied over the type's defaults, and anything
 * unrecognized is dropped. Returns the entry plus any unknown keys, so the caller can log
 * backend drift rather than silently discard a field on the next save.
 */
export function normalizeFromServer(entry: CRMIntegration): { integration: CRMIntegration; unknownKeys: string[] } {
	const source: Record<string, unknown> = entry;
	const result: Record<string, unknown> = defaultsForType(entry.type);
	result.enabled = entry.enabled === true;

	const specs = CRM_INTEGRATION_FIELDS[entry.type];
	specs.forEach((field) => {
		const value = source[field.key];
		if (value === undefined || value === null) {
			return;
		}
		result[field.key] = coerceToKind(field.kind, value);
	});

	const known = new Set(["type", "enabled", ...specs.map((field) => field.key)]);
	const unknownKeys = Object.keys(source).filter((key) => !known.has(key));

	return { integration: result as CRMIntegration, unknownKeys };
}

/**
 * Mirrors the server's per-type rules so a save is never rejected by something the form
 * could have caught. Returns a human-readable reason, or null when the entry is valid.
 */
export function validateIntegration(integration: CRMIntegration, position: number): string | null {
	const source: Record<string, unknown> = integration;
	const where = `${CRM_INTEGRATION_LABELS[integration.type]} #${position}`;

	for (const field of CRM_INTEGRATION_FIELDS[integration.type]) {
		const value = source[field.key];

		switch (field.kind) {
			case "string": {
				const trimmed = typeof value === "string" ? value.trim() : "";
				if (trimmed === "" && field.required) {
					return `${where}: ${field.label} is required`;
				}
				break;
			}
			case "url": {
				const trimmed = typeof value === "string" ? value.trim() : "";
				if (trimmed === "") {
					if (field.required) {
						return `${where}: ${field.label} is required`;
					}
					break;
				}
				if (!isHttpUrl(trimmed)) {
					return `${where}: ${field.label} must be a valid http(s) URL`;
				}
				break;
			}
			case "slug": {
				const trimmed = typeof value === "string" ? value.trim() : "";
				if (trimmed === "") {
					if (field.required) {
						return `${where}: ${field.label} is required`;
					}
					break;
				}
				if (!CRM_INTEGRATION_SLUG_PATTERN.test(trimmed)) {
					return `${where}: ${field.label} may only contain lowercase letters, numbers and hyphens`;
				}
				break;
			}
			case "number":
				if (typeof value !== "number" || !Number.isFinite(value)) {
					return `${where}: ${field.label} must be a number`;
				}
				break;
			case "estimateTypes": {
				if (!Array.isArray(value)) {
					return `${where}: ${field.label} must be a list`;
				}
				const invalid = value.filter((entry) => !ESTIMATE_TYPE_VALUES.has(entry));
				if (invalid.length > 0) {
					return `${where}: ${field.label} contains invalid values: ${invalid.join(", ")}`;
				}
				break;
			}
			case "boolean":
				break;
		}
	}

	return null;
}

/**
 * Ordinal of each entry *within its own type*, so a card labelled "PSA #2" really is the
 * second PSA rather than the second integration overall.
 */
export function typeOrdinals(integrations: CRMIntegration[]): number[] {
	const seen = new Map<CRMIntegrationType, number>();
	return integrations.map((integration) => {
		const ordinal = (seen.get(integration.type) ?? 0) + 1;
		seen.set(integration.type, ordinal);
		return ordinal;
	});
}

/** Keys that must render read-only on an integration that already exists on the server. */
export function lockedKeysFor(type: CRMIntegrationType): readonly string[] {
	return CRM_INTEGRATION_FIELDS[type].filter((field) => field.lockedReason).map((field) => field.key);
}

/**
 * Enforces the descriptor's `unique` flag
 */
export function duplicateValueError(integrations: CRMIntegration[]): string | null {
	const seen = new Map<string, Set<string>>();

	for (const integration of integrations) {
		const source: Record<string, unknown> = integration;
		for (const field of CRM_INTEGRATION_FIELDS[integration.type]) {
			if (!field.unique) {
				continue;
			}
			const value = source[field.key];
			const trimmed = typeof value === "string" ? value.trim() : "";
			if (trimmed === "") {
				continue;
			}
			const scope = `${integration.type}.${field.key}`;
			const used = seen.get(scope) ?? new Set<string>();
			if (used.has(trimmed)) {
				const label = CRM_INTEGRATION_LABELS[integration.type];
				return `Each ${label} integration needs a distinct ${field.label} (duplicate: ${trimmed}).`;
			}
			used.add(trimmed);
			seen.set(scope, used);
		}
	}

	return null;
}

/**
 * Integration types with more than one *enabled* entry.
 *
 * A type with a unique field is addressable per entry.
 */
export function duplicateEnabledTypes(integrations: CRMIntegration[]): CRMIntegrationType[] {
	const counts = new Map<CRMIntegrationType, number>();
	integrations.forEach((integration) => {
		const addressablePerEntry = CRM_INTEGRATION_FIELDS[integration.type].some((field) => field.unique);
		if (integration.enabled && !addressablePerEntry) {
			counts.set(integration.type, (counts.get(integration.type) ?? 0) + 1);
		}
	});
	return [...counts.entries()].filter(([, count]) => count > 1).map(([type]) => type);
}
